import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Plus, Edit, Trash2, DollarSign, Loader2, Flower2, Lamp, Frame, TableProperties, Tag } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Decor = () => {
  const [decorItems, setDecorItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    category: 'centerpieces',
    description: '',
    price: '',
    unit: 'each',
    color_options: '',
    available_quantity: '',
    notes: ''
  });

  useEffect(() => {
    fetchDecorItems();
  }, []);

  const fetchDecorItems = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/decor`);
      setDecorItems(response.data);
    } catch (error) {
      toast({ title: 'Error loading decor items', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        available_quantity: formData.available_quantity ? parseInt(formData.available_quantity) : null,
        color_options: formData.color_options ? formData.color_options.split(',').map(c => c.trim()) : []
      };

      if (editingItem) {
        await axios.put(`${API}/decor/${editingItem.id}`, data);
        toast({ title: 'Decor item updated!' });
      } else {
        await axios.post(`${API}/decor`, data);
        toast({ title: 'Decor item added!' });
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchDecorItems();
    } catch (error) {
      toast({ title: 'Error saving decor item', description: error.message, variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      category: 'centerpieces', 
      description: '', 
      price: '', 
      unit: 'each', 
      color_options: '', 
      available_quantity: '',
      notes: ''
    });
    setEditingItem(null);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      description: item.description || '',
      price: item.price.toString(),
      unit: item.unit,
      color_options: item.color_options?.join(', ') || '',
      available_quantity: item.available_quantity?.toString() || '',
      notes: item.notes || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this decor item?')) return;
    try {
      await axios.delete(`${API}/decor/${id}`);
      toast({ title: 'Decor item deleted!' });
      fetchDecorItems();
    } catch (error) {
      toast({ title: 'Error deleting decor item', description: error.message, variant: 'destructive' });
    }
  };

  const filteredItems = categoryFilter === 'all' ? decorItems : decorItems.filter(item => item.category === categoryFilter);

  const categoryLabels = {
    centerpieces: { label: 'Centerpieces', icon: Flower2 },
    florals: { label: 'Florals & Greenery', icon: Flower2 },
    lighting: { label: 'Lighting & Candles', icon: Lamp },
    backdrops: { label: 'Backdrops & Arches', icon: Frame },
    table_decor: { label: 'Table Decor', icon: TableProperties },
    signage: { label: 'Signage & Displays', icon: Tag }
  };

  const getCategoryIcon = (category) => {
    const Icon = categoryLabels[category]?.icon || Flower2;
    return <Icon size={16} />;
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-[#faf9f6] min-h-screen" data-testid="decor-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#1e3a5f] mb-1">Decor Management</h1>
          <p className="text-gray-600">Manage decorative items for events</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white" data-testid="add-decor-btn">
              <Plus size={20} className="mr-2" />
              Add Decor Item
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Decor Item' : 'Add New Decor Item'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="decor-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Item Name *</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })} 
                    required 
                    data-testid="decor-name-input"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger data-testid="decor-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="centerpieces">Centerpieces</SelectItem>
                      <SelectItem value="florals">Florals & Greenery</SelectItem>
                      <SelectItem value="lighting">Lighting & Candles</SelectItem>
                      <SelectItem value="backdrops">Backdrops & Arches</SelectItem>
                      <SelectItem value="table_decor">Table Decor</SelectItem>
                      <SelectItem value="signage">Signage & Displays</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
                  rows={2}
                  data-testid="decor-description-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Price ($) *</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={formData.price} 
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })} 
                    required 
                    data-testid="decor-price-input"
                  />
                </div>
                <div>
                  <Label>Unit *</Label>
                  <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="each">Each</SelectItem>
                      <SelectItem value="set">Set</SelectItem>
                      <SelectItem value="pair">Pair</SelectItem>
                      <SelectItem value="dozen">Dozen</SelectItem>
                      <SelectItem value="event">Per Event</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Available Qty</Label>
                  <Input 
                    type="number" 
                    value={formData.available_quantity} 
                    onChange={(e) => setFormData({ ...formData, available_quantity: e.target.value })} 
                    data-testid="decor-quantity-input"
                  />
                </div>
              </div>
              <div>
                <Label>Color Options (comma-separated)</Label>
                <Input 
                  placeholder="Gold, Silver, Rose Gold, White" 
                  value={formData.color_options} 
                  onChange={(e) => setFormData({ ...formData, color_options: e.target.value })} 
                  data-testid="decor-colors-input"
                />
              </div>
              <div>
                <Label>Additional Notes</Label>
                <Textarea 
                  placeholder="Setup requirements, special instructions, etc." 
                  value={formData.notes} 
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })} 
                  rows={2}
                  data-testid="decor-notes-input"
                />
              </div>
              <Button type="submit" className="w-full bg-[#c9a227] hover:bg-[#c4a574] text-[#1e3a5f] font-semibold" data-testid="decor-submit-btn">
                {editingItem ? 'Update Item' : 'Add Item'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={categoryFilter} onValueChange={setCategoryFilter} className="mb-6">
        <TabsList className="bg-white">
          <TabsTrigger value="all" data-testid="filter-all">All Items</TabsTrigger>
          <TabsTrigger value="centerpieces" data-testid="filter-centerpieces">Centerpieces</TabsTrigger>
          <TabsTrigger value="florals" data-testid="filter-florals">Florals</TabsTrigger>
          <TabsTrigger value="lighting" data-testid="filter-lighting">Lighting</TabsTrigger>
          <TabsTrigger value="backdrops" data-testid="filter-backdrops">Backdrops</TabsTrigger>
          <TabsTrigger value="table_decor" data-testid="filter-table-decor">Table Decor</TabsTrigger>
          <TabsTrigger value="signage" data-testid="filter-signage">Signage</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#1e3a5f]" size={48} /></div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-20">
          <Flower2 size={64} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-xl font-semibold text-gray-500 mb-2">No Decor Items Found</h3>
          <p className="text-gray-400 mb-4">Add your first decor item to get started</p>
          <Button 
            onClick={() => setIsDialogOpen(true)} 
            className="bg-[#1e3a5f] hover:bg-[#2d5016] text-white"
          >
            <Plus size={20} className="mr-2" />
            Add Decor Item
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <Card key={item.id} className="border-none shadow-lg hover:shadow-xl transition-all" data-testid={`decor-card-${item.id}`}>
              <CardHeader className="bg-gradient-to-r from-[#c4a574] to-[#c9a227] text-white rounded-t-lg">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {getCategoryIcon(item.category)}
                    {item.name}
                  </CardTitle>
                </div>
                <span className="text-xs opacity-90">{categoryLabels[item.category]?.label || item.category}</span>
              </CardHeader>
              <CardContent className="p-6">
                {item.description && <p className="text-sm text-gray-600 mb-4">{item.description}</p>}
                <div className="flex items-center space-x-2 text-[#1e3a5f] font-bold text-2xl mb-4">
                  <DollarSign size={20} />
                  <span>{item.price.toFixed(2)}</span>
                  <span className="text-sm font-normal text-gray-600">per {item.unit}</span>
                </div>
                {item.color_options && item.color_options.length > 0 && (
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-700 mb-2">COLORS:</p>
                    <div className="flex flex-wrap gap-1">
                      {item.color_options.map((color, idx) => (
                        <span key={idx} className="px-2 py-1 bg-[#f5f5dc] text-xs rounded border border-[#c9a227]">{color}</span>
                      ))}
                    </div>
                  </div>
                )}
                {item.available_quantity && (
                  <p className="text-xs text-gray-600 mb-2">Available: {item.available_quantity}</p>
                )}
                {item.notes && (
                  <div className="mb-4 p-2 bg-yellow-50 rounded border-l-2 border-yellow-400">
                    <p className="text-xs text-gray-600">{item.notes}</p>
                  </div>
                )}
                <div className="flex space-x-2">
                  <Button 
                    onClick={() => handleEdit(item)} 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white"
                    data-testid={`edit-decor-${item.id}`}
                  >
                    <Edit size={16} className="mr-1" />Edit
                  </Button>
                  <Button 
                    onClick={() => handleDelete(item.id)} 
                    variant="outline" 
                    size="sm" 
                    className="flex-1 border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    data-testid={`delete-decor-${item.id}`}
                  >
                    <Trash2 size={16} className="mr-1" />Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Decor;
